import {
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { CenteredDialogContent } from '#components/centered-dialog-content/centered-dialog-content';

export type GoogleCapabilitySelection = {
  gmail: boolean;
  calendar: boolean;
};

export interface GoogleOAuthConnectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSelection: GoogleCapabilitySelection;
  mode: 'connect' | 'reconnect';
  onConfirm: (selection: GoogleCapabilitySelection) => void;
}

function CapabilityToggle({
  title,
  description,
  selected,
  onToggle,
}: {
  title: string;
  description: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <Button
      variant={selected ? 'solid' : 'outline'}
      colorPalette={selected ? 'blue' : undefined}
      justifyContent="flex-start"
      h="auto"
      py={3}
      px={4}
      onClick={onToggle}
    >
      <VStack align="start" gap={1}>
        <Text fontSize="sm" fontWeight="medium">
          {title}
        </Text>
        <Text fontSize="xs" color={selected ? 'whiteAlpha.900' : 'fg.muted'}>
          {description}
        </Text>
      </VStack>
    </Button>
  );
}

export function GoogleOAuthConnectModal({
  open,
  onOpenChange,
  initialSelection,
  mode,
  onConfirm,
}: GoogleOAuthConnectModalProps) {
  const [selection, setSelection] = useState(initialSelection);

  useEffect(() => {
    if (!open) return;

    setSelection(initialSelection);
  }, [initialSelection, open]);

  const hasAtLeastOne = selection.gmail || selection.calendar;

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onOpenChange(false);
      }}
    >
      <CenteredDialogContent maxW="min(92vw, 32rem)">
        <DialogHeader>
          <DialogTitle>
            {mode === 'connect' ? 'Connect Google' : 'Reconnect Google'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={3}>
            <Text fontSize="sm" color="fg.muted">
              Choose which Google data this connection can access. You can
              reconnect later to add or remove permissions.
            </Text>
            <CapabilityToggle
              title="Email Contents"
              description="Read email message metadata and bodies from Gmail."
              selected={selection.gmail}
              onToggle={() =>
                setSelection((current) => ({
                  ...current,
                  gmail: !current.gmail,
                }))
              }
            />
            <CapabilityToggle
              title="Calendar"
              description="Read calendar events and schedules from Google Calendar."
              selected={selection.calendar}
              onToggle={() =>
                setSelection((current) => ({
                  ...current,
                  calendar: !current.calendar,
                }))
              }
            />
            <Text fontSize="xs" color="fg.muted">
              At least one capability is required.
            </Text>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="blue"
              disabled={!hasAtLeastOne}
              onClick={() => onConfirm(selection)}
            >
              {mode === 'connect'
                ? 'Continue to Google'
                : 'Reconnect in Google'}
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
